import React, { useState } from 'react';
import styles from './structure-grid.module.scss';
import classNames from 'classnames';

import { RadioButton, Checkbox } from '@carbon/react';
import { CaretDown, CaretRight } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import style from '../map/map.module.scss';
import { MapUserConfigAtom } from 'components/map/types';
import { useStructureGrids } from './use-structure-grids';

export const StructureGridSelector = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);
  const [expandedBasins, setExpandedBasins] = useState<{
    [key: string]: boolean;
  }>({});
  const [isGridExpanded, setIsGridExpanded] = useState(false);
  const [expandedFormations, setExpandedFormations] = useState<{
    [key: string]: boolean;
  }>({});

  // Use the useStructureGrids hook to get API data
  const { isBasinsLoading, availableBasins, getFormationsForBasin } =
    useStructureGrids();

  // Helper functions to get state
  const isBasinExpanded = (basinId: string) => !!expandedBasins[basinId];

  const getSelectedFormation = (basinId: string) => {
    return mapConfig.structureGrids?.selections?.[basinId] || '';
  };

  const isFormationSelected = (basinId: string, formationId: string) => {
    return getSelectedFormation(basinId) === formationId;
  };

  const isOptionEnabled = (
    basinId: string,
    formationId: string,
    optionId: 'structure' | 'contour'
  ): boolean => {
    const key = `${basinId}-${formationId}`;
    return mapConfig.structureGrids?.options?.[key]?.[optionId] ?? false;
  };

  const getSelectionCount = () => {
    const selections = mapConfig.structureGrids?.selections || {};
    return Object.values(selections).filter(Boolean).length;
  };

  // Toggle basin expansion
  const toggleBasinExpansion = (basinId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedBasins((prev) => ({
      ...prev,
      [basinId]: !prev[basinId],
    }));
  };

  const selectFormation = (basinId: string, formationId: string) => {
    const formationKey = `${basinId}-${formationId}`;

    // Update state
    setMapConfig((current: any) => {
      // First clear any previous selection in this basin
      const currentSelection = current.structureGrids?.selections?.[basinId];
      const currentOptions = current.structureGrids?.options || {};

      // Clear previous selection's options if different
      if (currentSelection && currentSelection !== formationId) {
        const prevKey = `${basinId}-${currentSelection}`;
        delete currentOptions[prevKey];
      }

      // Set new selection with default options
      return {
        ...current,
        structureGrids: {
          ...(current.structureGrids || {}),
          checked: true,
          selections: {
            ...(current.structureGrids?.selections || {}),
            [basinId]: formationId,
          },
          options: {
            ...currentOptions,
            [formationKey]: {
              structure: true,
              contour: true,
            },
          },
        },
      };
    });
  };

  const toggleOption = (
    basinId: string,
    formationId: string,
    optionId: string
  ) => {
    const formationKey = `${basinId}-${formationId}`;
    const currentlyEnabled = isOptionEnabled(
      basinId,
      formationId,
      optionId as 'structure' | 'contour'
    );

    // Update state
    setMapConfig((current: any) => {
      const currentOptions = current.structureGrids?.options || {};
      const formationOptions = currentOptions[formationKey] || {};

      return {
        ...current,
        structureGrids: {
          ...(current.structureGrids || {}),
          checked: true,
          options: {
            ...currentOptions,
            [formationKey]: {
              ...formationOptions,
              [optionId]: !currentlyEnabled,
            },
          },
        },
      };
    });
  };

  const clearBasin = (basinId: string) => {
    setMapConfig((current: any) => {
      const selections = { ...(current.structureGrids?.selections || {}) };
      const options = { ...(current.structureGrids?.options || {}) };

      // Remove this basin's selection
      delete selections[basinId];

      // Remove all options for this basin
      Object.keys(options).forEach((key) => {
        if (key.startsWith(`${basinId}-`)) {
          delete options[key];
        }
      });

      // Check if we still have any selections
      const hasSelections = Object.keys(selections).length > 0;

      return {
        ...current,
        structureGrids: {
          ...(current.structureGrids || {}),
          checked: hasSelections, // Disable if no selections remain
          selections,
          options,
        },
      };
    });
  };

  const clearAll = () => {
    setMapConfig((current: any) => ({
      ...current,
      structureGrids: {
        ...(current.structureGrids || {}),
        checked: false, // Disable structure grids when clearing all
        selections: {},
        options: {},
      },
    }));
  };

  const toggleFormationExpansion = (
    formationKey: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setExpandedFormations((prev) => ({
      ...prev,
      [formationKey]: !prev[formationKey],
    }));
  };

  const renderBasin = (basin: { id: string; name: string }) => {
    const { id: basinId, name: basinName } = basin;
    const formations = getFormationsForBasin(basinId);
    const isExpanded = isBasinExpanded(basinId);
    const selectedFormation = getSelectedFormation(basinId);
    const hasSelection = !!selectedFormation;

    return (
      <div key={basinId} className={styles.basinContainer}>
        <div
          onClick={(e) => toggleBasinExpansion(basinId, e)}
          className={classNames(styles.basinHeader, {
            [styles.basinHeaderExpanded]: hasSelection,
          })}
        >
          {isExpanded ? <CaretDown /> : <CaretRight />}
          <span className={styles.basinName}>
            {basinName} {hasSelection ? `(1)` : ''}
          </span>

          {hasSelection && (
            <button
              className={styles.clearButton}
              onClick={(e) => {
                e.stopPropagation();
                clearBasin(basinId);
              }}
            >
              Clear
            </button>
          )}
        </div>
        {isExpanded && (
          <div className={styles.formationsContainer}>
            {formations.map((formation: { id: number; name: string }) => {
              const formationIdString = formation.id.toString();
              const isSelected = isFormationSelected(
                basinId,
                formationIdString
              );
              const formationKey = `${basinId}-${formationIdString}`;
              const isFormationExpanded = expandedFormations[formationKey];

              return (
                <div key={formation.id} className={style.radioChecks}>
                  <div
                    className={classNames(styles.formationHeader, {
                      [styles.formationHeaderExpanded]: isSelected,
                    })}
                  >
                    <RadioButton
                      value={formation.name}
                      checked={isSelected}
                      className={`${styles.radioButtonWrapper}`}
                      onChange={() =>
                        selectFormation(basinId, formationIdString)
                      }
                      labelText={
                        <div
                          className={styles.radioLabel}
                          onClick={(e) =>
                            toggleFormationExpansion(formationKey, e)
                          }
                        >
                          {isFormationExpanded && isSelected ? (
                            <CaretDown />
                          ) : (
                            <CaretRight />
                          )}
                          <span className={styles.labelText}>
                            {formation.name}
                          </span>
                        </div>
                      }
                      name={`radio-group-${basinId}`}
                    />
                  </div>
                  {isFormationExpanded && isSelected && (
                    <div className={`${styles.groupChecksCheckStructureGrids}`}>
                      <div
                        className={classNames(styles.checkboxWrapper, {
                          [styles.groupCheckBoxOnselected]: isOptionEnabled(
                            basinId,
                            formationIdString,
                            'structure'
                          ),
                        })}
                      >
                        <Checkbox
                          id={`structure-${formation.id}`}
                          checked={isOptionEnabled(
                            basinId,
                            formationIdString,
                            'structure'
                          )}
                          onChange={() =>
                            toggleOption(
                              basinId,
                              formationIdString,
                              'structure'
                            )
                          }
                          labelText={'Structure'}
                        />
                      </div>
                      <div
                        className={classNames(styles.checkboxWrapper, {
                          [styles.groupCheckBoxOnselected]: isOptionEnabled(
                            basinId,
                            formationIdString,
                            'contour'
                          ),
                        })}
                      >
                        <Checkbox
                          className={`${style.reviewField}`}
                          id={`contour-${formation.id}`}
                          checked={isOptionEnabled(
                            basinId,
                            formationIdString,
                            'contour'
                          )}
                          onChange={() =>
                            toggleOption(basinId, formationIdString, 'contour')
                          }
                          labelText={'Contour'}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const selectionCount = getSelectionCount();

  const toggleGridExpansion = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsGridExpanded(!isGridExpanded);
  };

  if (isBasinsLoading) {
    return (
      <div className={styles.loadingIndicator}>Loading structure grids...</div>
    );
  }

  return (
    <>
      <div className={styles.gridHeader} onClick={toggleGridExpansion}>
        <div
          className={classNames(styles.gridTitle, {
            [styles.gridTitleExpanded]: selectionCount > 0,
          })}
        >
          {isGridExpanded ? (
            <CaretDown className={style.collapseIcon} />
          ) : (
            <CaretRight className={style.collapseIcon} />
          )}
          <span>
            Structure Grids {selectionCount > 0 ? `(${selectionCount})` : ''}
          </span>
        </div>

        {selectionCount > 0 && (
          <button
            className={styles.clearAllButton}
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
          >
            Clear All
          </button>
        )}
      </div>
      {isGridExpanded && (
        <div className={styles.gridContent}>
          {availableBasins.map(renderBasin)}
        </div>
      )}
    </>
  );
};
